// The company's signature rule (D18) as the signatory change editor and the signing cards use it: a group at the top, a
// k-of-n threshold of people and further groups at every level, to any depth, each group with an optional name, and a
// person allowed in more than one group. It mirrors the rule model in ns-lambda-ts (_core/public-ledger/public-ledger.ts:
// validateRule, buildAccountRequirement, isRuleSatisfied), which has the final say. Pure logic only, so it is unit tested.

import type { Toast } from '../../../../ltn-toast/ltn-toast-service.js';
import { lambdaErrorCode } from '../../../../helpers/lambda.js';
import { jobLedgerGate, waitingOverlay } from '../../../../helpers/job-ledger-gate.js';

export interface RulePerson {
  keyType: 'PERSON_ID';
  signatoryId: string;
}

export interface RuleGroup {
  keyType: 'THRESHOLD_KEY';
  name: string | null;
  threshold: number;
  keys: RuleNode[];
}

export type RuleNode = RuleGroup | RulePerson;

// The place of a node in the rule: the index of each key from the top group down. The top group is []
export type RulePath = number[];

// The ledger's limits, as ns-lambda-ts enforces them: groups nested at most 10 deep (the top counts), at most 100 people
// named (each place a person is named counts), and group names of up to 100 characters
export const MAX_RULE_DEPTH = 10;
export const MAX_RULE_KEYS = 100;
export const MAX_GROUP_NAME_LENGTH = 100;

interface StoredKey {
  name?: string | null;
  keyType?: string;
  threshold?: number | null;
  signatoryId?: string | null;
  keys?: StoredKey[] | null;
}

/**
 * The editable rule from a stored signatureRequirement: a KEY_LIST (or a group with no threshold) needs every key, so it
 * reads as the threshold of all of them. Keys the rule can't hold (a raw ED25519 key, an unknown type) are left out.
 * Returns null when there is no rule.
 */
export function ruleFromRequirement(requirement: StoredKey | null | undefined): RuleGroup | null {
  if (!requirement || typeof requirement !== 'object') return null;
  const visit = (key: StoredKey): RuleNode | null => {
    if (key.keyType === 'PERSON_ID') {
      return key.signatoryId ? { keyType: 'PERSON_ID', signatoryId: key.signatoryId } : null;
    }
    if (key.keyType !== 'THRESHOLD_KEY' && key.keyType !== 'KEY_LIST') return null;
    const keys = (key.keys ?? []).map(visit).filter((k): k is RuleNode => k !== null);
    const threshold = key.keyType === 'KEY_LIST' || typeof key.threshold !== 'number' ? keys.length : key.threshold;
    return { keyType: 'THRESHOLD_KEY', name: key.name?.trim() || null, threshold, keys };
  };
  const top = visit(requirement);
  if (!top) return null;
  if (top.keyType === 'PERSON_ID') return { keyType: 'THRESHOLD_KEY', name: null, threshold: 1, keys: [top] };
  return top;
}

/** A threshold within 1..n (1 for an empty group, which validateRule refuses anyway); whole numbers only. */
export function clampThreshold(k: number, n: number): number {
  if (n < 1) return 1;
  if (!Number.isFinite(k)) return 1;
  return Math.min(n, Math.max(1, Math.round(k)));
}

/** The node at a path, or null when the path doesn't lead to one. */
export function nodeAt(rule: RuleGroup, path: RulePath): RuleNode | null {
  let node: RuleNode = rule;
  for (const idx of path) {
    if (node.keyType !== 'THRESHOLD_KEY') return null;
    const next: RuleNode | undefined = node.keys[idx];
    if (!next) return null;
    node = next;
  }
  return node;
}

// A copy of the rule with the group at the path changed. Paths that don't lead to a group leave the rule as it was
function updateGroup(rule: RuleGroup, path: RulePath, change: (group: RuleGroup) => RuleGroup): RuleGroup {
  if (path.length === 0) return change(rule);
  const [idx, ...rest] = path;
  const child = rule.keys[idx as number];
  if (!child || child.keyType !== 'THRESHOLD_KEY') return rule;
  const keys = [...rule.keys];
  keys[idx as number] = updateGroup(child, rest, change);
  return { ...rule, keys };
}

/** Add a person to the group at the path. A person already named directly in that group isn't added twice. */
export function addPerson(rule: RuleGroup, path: RulePath, personId: string): RuleGroup {
  return updateGroup(rule, path, (group) =>
    group.keys.some((k) => k.keyType === 'PERSON_ID' && k.signatoryId === personId)
      ? group
      : { ...group, keys: [...group.keys, { keyType: 'PERSON_ID', signatoryId: personId }] },
  );
}

/** Add an empty named group, needing 1 signature, to the group at the path. */
export function addGroup(rule: RuleGroup, path: RulePath, name: string | null = null): RuleGroup {
  return updateGroup(rule, path, (group) => ({
    ...group,
    keys: [...group.keys, { keyType: 'THRESHOLD_KEY', name: name?.trim() || null, threshold: 1, keys: [] }],
  }));
}

/** Rename the group at the path; an empty name clears it. */
export function renameGroup(rule: RuleGroup, path: RulePath, name: string): RuleGroup {
  // The field shows the set-up's name as empty, so leaving it empty keeps it rather than changing the rule
  return updateGroup(rule, path, (group) => ({
    ...group,
    name: name.trim() || (group.name === SET_UP_RULE_NAME ? SET_UP_RULE_NAME : null),
  }));
}

/** The name the company's set-up gives its top rule: internal, so it is never shown as the rule's name. */
export const SET_UP_RULE_NAME = 'MAIN_KEY';

/** A group's name as people see it: empty for none, and for the set-up's internal name. */
export function shownGroupName(name: string | null | undefined): string {
  return name && name !== SET_UP_RULE_NAME ? name : '';
}

/** Set how many of its keys the group at the path needs, clamped to 1..n. */
export function setThreshold(rule: RuleGroup, path: RulePath, threshold: number): RuleGroup {
  return updateGroup(rule, path, (group) => ({ ...group, threshold: clampThreshold(threshold, group.keys.length) }));
}

/** Remove the person or group at the path (never the top group); the group it was in keeps a threshold within 1..n. */
export function removeNode(rule: RuleGroup, path: RulePath): RuleGroup {
  if (path.length === 0) return rule;
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1] as number;
  return updateGroup(rule, parentPath, (group) => {
    if (!group.keys[idx]) return group;
    const keys = group.keys.filter((_, i) => i !== idx);
    return { ...group, keys, threshold: clampThreshold(group.threshold, keys.length) };
  });
}

/** Everyone the rule names, each once, in the order first named. */
export function ruleSignatoryIds(rule: RuleNode | null | undefined): string[] {
  const ids: string[] = [];
  const visit = (key: RuleNode) => {
    if (key.keyType === 'PERSON_ID') {
      if (!ids.includes(key.signatoryId)) ids.push(key.signatoryId);
    } else {
      key.keys.forEach(visit);
    }
  };
  if (rule) visit(rule);
  return ids;
}

/** Whether this person is named in the company's signature rule. */
export function isNamedSignatory(requirement: unknown, personId: string | null | undefined): boolean {
  if (!personId) return false;
  const rule = ruleFromRequirement(requirement as Parameters<typeof ruleFromRequirement>[0]);
  return ruleSignatoryIds(rule).includes(personId);
}

/**
 * Why the rule can't be requested, or null when it can, as ns-lambda-ts validateRule checks it: every group holds at least
 * one key and needs 1 to n of them, and the rule is within the ledger's depth, size and name limits.
 */
export function validateRule(rule: RuleGroup): string | null {
  let people = 0;
  const visit = (key: RuleNode, depth: number, label: string): string | null => {
    if (key.keyType === 'PERSON_ID') {
      people++;
      if (people > MAX_RULE_KEYS) {
        return `A rule can name at most ${MAX_RULE_KEYS} signatories, counting each group a person is in`;
      }
      return null;
    }
    if (depth > MAX_RULE_DEPTH) return `Groups can be nested at most ${MAX_RULE_DEPTH} deep`;
    if ((key.name ?? '').length > MAX_GROUP_NAME_LENGTH) {
      return `A group name can be at most ${MAX_GROUP_NAME_LENGTH} characters`;
    }
    if (key.keys.length === 0) return `${label} needs at least one signatory or group`;
    if (!Number.isInteger(key.threshold) || key.threshold < 1 || key.threshold > key.keys.length) {
      return `${label} needs between 1 and ${key.keys.length} signatures`;
    }
    for (const child of key.keys) {
      const problem = visit(child, depth + 1, child.keyType === 'THRESHOLD_KEY' ? groupLabel(child) : label);
      if (problem) return problem;
    }
    return null;
  };
  return visit(rule, 1, shownGroupName(rule.name) ? `"${shownGroupName(rule.name)}"` : 'The rule');
}

const groupLabel = (group: RuleGroup) =>
  shownGroupName(group.name) ? `"${shownGroupName(group.name)}"` : 'A group with no name';

// The rule in one comparable form
function canonical(rule: RuleNode): unknown {
  if (rule.keyType === 'PERSON_ID') return { person: rule.signatoryId };
  return { name: rule.name || null, threshold: rule.threshold, keys: rule.keys.map(canonical) };
}

/** Whether two rules are the same: groups, names, thresholds and people. */
export function isSameRule(a: RuleGroup | null, b: RuleGroup | null): boolean {
  if (!a || !b) return a === b;
  return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
}

/** The rule as public-ledger-request-signatory-change and update-threshold-key take it. */
export function toRequestRule(rule: RuleGroup): RuleGroup {
  return JSON.parse(JSON.stringify(rule)) as RuleGroup;
}

/**
 * Whether these people's signatures meet the rule: a group is met when at least its threshold of its keys are. A person in
 * two groups counts in both, as on the public ledger.
 */
export function isRuleSatisfied(rule: RuleNode | null, signerIds: Iterable<string>): boolean {
  if (!rule) return false;
  const signers = new Set(signerIds);
  const met = (key: RuleNode): boolean => {
    if (key.keyType === 'PERSON_ID') return signers.has(key.signatoryId);
    if (key.keys.length === 0 || key.threshold < 1) return false;
    return key.keys.filter(met).length >= key.threshold;
  };
  return met(rule);
}

/** The names of the groups a person is named in, top first, for "You sign as part of ..." on a signing card. */
export function groupsOfSigner(rule: RuleNode | null, personId: string): string[] {
  const names: string[] = [];
  const visit = (key: RuleNode) => {
    if (key.keyType === 'PERSON_ID') return;
    const name = shownGroupName(key.name);
    if (key.keys.some((k) => k.keyType === 'PERSON_ID' && k.signatoryId === personId) && name) {
      if (!names.includes(name)) names.push(name);
    }
    key.keys.forEach(visit);
  };
  if (rule) visit(rule);
  return names;
}

/**
 * One line that says what the rule needs, for example "2 of: Alice Smith, Bob Jones, Finance (1 of: Carol Ng, Dan Ode)".
 * A group needing every key of several reads "All of", and a group of one reads as that key alone.
 */
export function summariseRule(rule: RuleNode | null, nameOf: (personId: string) => string): string {
  if (!rule) return 'No signatories';
  const visit = (key: RuleNode, top: boolean): string => {
    if (key.keyType === 'PERSON_ID') return nameOf(key.signatoryId);
    const items = key.keys.map((k) => visit(k, false));
    let body: string;
    if (items.length === 0) body = 'no signatories';
    else if (items.length === 1) body = items[0] as string;
    else body = `${key.threshold === items.length ? 'All' : key.threshold} of: ${items.join(', ')}`;
    const name = shownGroupName(key.name);
    if (top) return name ? `${name}: ${body}` : body;
    return name ? `${name} (${body})` : `(${body})`;
  };
  return visit(rule, true);
}

export interface SignatoryChangeAccess {
  // Whether the pen that starts the change shows at all: only to the people who may start it
  visible: boolean;
  allowed: boolean;
  // Why the change can't be started, shown beside the disabled button; null when it can
  reason: string | null;
}

/**
 * Whether this person can start a signatory change (D8, Q7): only a current signatory, while no change is open and no
 * signatures are pending for the company. The pen shows to signatories only (#38). The lambda checks the same again.
 */
export function signatoryChangeAccess(input: {
  isSignatory: boolean;
  changeOpen: boolean;
  pendingSignatures: boolean;
}): SignatoryChangeAccess {
  if (!input.isSignatory) {
    return { visible: false, allowed: false, reason: 'Only a current signatory can change who signs' };
  }
  if (input.changeOpen) {
    return { visible: true, allowed: false, reason: 'Please wait: the signatory list is being changed' };
  }
  if (input.pendingSignatures) {
    return {
      visible: true,
      allowed: false,
      reason: 'Signatures are still being collected for this company. You can change who signs once they are done',
    };
  }
  return { visible: true, allowed: true, reason: null };
}

/**
 * Whether this person can start a change to who signs the company's topic messages (#38, DECISION 3a): only a current
 * signatory of the topic, once the company has a topic, while no topic change is open and no company topic messages are
 * still being signed. The pen shows to the topic's signatories only. public-ledger-request-topic-signatory-change checks
 * the same again.
 */
export function topicSignatoryChangeAccess(input: {
  hasTopic: boolean;
  isTopicSignatory: boolean;
  changeOpen: boolean;
  pendingSignatures: boolean;
}): SignatoryChangeAccess {
  if (!input.hasTopic) {
    return { visible: false, allowed: false, reason: 'Your company has no public ledger topic yet' };
  }
  if (!input.isTopicSignatory) {
    return {
      visible: false,
      allowed: false,
      reason: "Only a current signatory of the company's topic can change who signs its messages",
    };
  }
  if (input.changeOpen) {
    return {
      visible: true,
      allowed: false,
      reason: "Please wait: who signs the company's topic messages is being changed",
    };
  }
  if (input.pendingSignatures) {
    return {
      visible: true,
      allowed: false,
      reason: 'Company topic messages are still being signed. You can change who signs them once they are done',
    };
  }
  return { visible: true, allowed: true, reason: null };
}

/**
 * The error toast for a refused change request (#38), for both pens: the server's reason when it gave one. The server is
 * the gate, so whatever the page showed, a refusal ends here. #41: a change refused because the company's automatic
 * payments aren't on is headed with its wait ("Waiting for automatic payments"), the rest of the server's wording below it;
 * the person is notified once they can change it.
 */
export function changeRefusalToast(err: unknown): Toast {
  const gate = jobLedgerGate(lambdaErrorCode(err), err instanceof Error ? err.message : null);
  const overlay = gate ? waitingOverlay(gate) : null;
  if (overlay) return { variant: 'danger', label: overlay.title, description: overlay.paragraphs.join(' ') };
  let description = 'Please try again.';
  if (err instanceof Error && err.message) description = err.message;
  else if (typeof err === 'string' && err.trim()) description = err;
  return { variant: 'danger', label: 'Unable to start the change', description };
}

// The statuses of a company's ledger rows that still collect its signatures (signing-locks.ts in ns-lambda-ts)
const PENDING_TRANSACTION_STATUSES = ['PENDING', 'COLLECTING_SIGNATURES'];

interface LockTransaction {
  status: string;
  type: string;
  topicType?: string | null;
  accountId?: string | null;
  transfer?: { fromAccountId?: string | null; kind?: string | null } | null;
}

interface LockMessage {
  status: string;
  topicType?: string | null;
}

/**
 * "Pending signatures for the company", the account pool in signing-locks.ts (Q7, Q17, #38 DECISION 6a), for the
 * client's own rows: a signatory change or an allowance transfer from the company account still collecting signatures.
 * Company topic messages are the topic pool's (hasPendingTopicSignatures): the account change no longer touches the
 * topic. `messages` is kept for its callers and no longer counts. The lambda's check is the one that counts.
 */
export function hasPendingCompanySignatures(
  transactions: LockTransaction[],
  _messages: LockMessage[],
  companyAccountId: string | null | undefined,
): boolean {
  return transactions.some((t) => {
    if (!PENDING_TRANSACTION_STATUSES.includes(t.status)) return false;
    if (t.type === 'UPDATE_ACCOUNT_TRANSACTION') return true;
    // An automatic top-up's lock row collects no signatures (the operating key alone signs it), as signing-locks.ts says
    if (t.type === 'TRANSFER_TRANSACTION') {
      return !!companyAccountId && t.transfer?.fromAccountId === companyAccountId && t.transfer?.kind !== 'AUTO_TOP_UP';
    }
    return false;
  });
}

/**
 * Company topic messages still being signed, the topic pool in signing-locks.ts (#38): a company-topic message still
 * collecting signatures, or a company-topic message request still queued. A change to who signs the topic waits for them.
 */
export function hasPendingTopicSignatures(transactions: LockTransaction[], messages: LockMessage[]): boolean {
  const pendingTransaction = transactions.some(
    (t) =>
      PENDING_TRANSACTION_STATUSES.includes(t.status) &&
      t.type === 'SUBMIT_TOPIC_MESSAGE_TRANSACTION' &&
      t.topicType === 'CORPORATE',
  );
  return pendingTransaction || messages.some((m) => m.status === 'QUEUED' && m.topicType === 'CORPORATE');
}

/**
 * Which workflow and card a signatory change started, from public-ledger-request-signatory-change's answer: its
 * `workflowId` and `jobId`, or the JOB_CREATED action of a workflow-creation result. Nulls when the answer names neither.
 */
export function signatoryChangeLaunch(res: unknown): { workflowId: string | null; jobId: string | null } {
  const result = { workflowId: null as string | null, jobId: null as string | null };
  if (typeof res !== 'object' || res === null) return result;
  const body = res as { workflowId?: unknown; jobId?: unknown; message?: unknown };
  if (typeof body.workflowId === 'string') result.workflowId = body.workflowId;
  if (typeof body.jobId === 'string') result.jobId = body.jobId;
  if (!result.jobId && Array.isArray(body.message)) {
    const created = body.message.find(
      (m): m is { action: string; id: string } =>
        typeof m === 'object' &&
        m !== null &&
        String((m as { action?: unknown }).action).toUpperCase() === 'JOB_CREATED',
    );
    if (created && typeof created.id === 'string') result.jobId = created.id;
  }
  return result;
}
